import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';

export const colorVariants = {
  primary: {
    default:
      'bg-brand text-brand-foreground border border-border-brand hover:bg-brand-hover focus-visible:bg-brand-focus active:bg-brand-focus inset-shadow-sm inset-shadow-white/22 dark:inset-shadow-white/18 before:absolute before:inset-0 before:bg-gradient-to-t before:from-transparent before:to-white/20 dark:before:to-white/12 before:pointer-events-none aria-invalid:border-border-destructive aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive:
      'bg-destructive text-destructive-foreground border border-border-destructive hover:bg-destructive-hover focus-visible:bg-destructive-focus active:bg-destructive-focus inset-shadow-sm inset-shadow-white/22 dark:inset-shadow-white/18 before:absolute before:inset-0 before:bg-gradient-to-t before:from-transparent before:to-white/20 dark:before:to-white/12 before:pointer-events-none',
  },
  secondary: {
    default:
      'bg-gray text-gray-foreground border-gray hover:bg-gray-hover focus-visible:bg-gray-focus active:bg-gray-focus inset-shadow-sm inset-shadow-white/22 dark:inset-shadow-white/18 before:absolute before:inset-0 before:bg-gradient-to-t before:from-transparent before:to-white/20 dark:before:to-white/12 before:pointer-events-none aria-invalid:border-border-destructive aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive:
      'bg-destructive text-destructive-foreground border-border-destructive hover:bg-destructive-hover focus-visible:bg-destructive-focus active:bg-destructive-focus inset-shadow-sm inset-shadow-white/22 dark:inset-shadow-white/18 before:absolute before:inset-0 before:bg-gradient-to-t before:from-transparent before:to-white/20 dark:before:to-white/12 before:pointer-events-none',
  },
  tertiary: {
    default:
      'bg-muted text-foreground border border-border-primary focus-visible:border-border-primary-focus aria-invalid:border-border-destructive/75 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive:
      'bg-destructive-accent text-destructive hover:bg-destructive-accent-hover border-transparent focus-visible:border focus-visible:border-border-destructive-accent active:bg-destructive-accent-hover',
  },
  outline: {
    default:
      'bg-input dark:bg-input/35 text-foreground border border-border-primary hover:bg-input-focus dark:hover:bg-input-focus/75 focus-visible:bg-bg-input-focus dark:focus-visible:bg-input-focus/25 focus-visible:border-border-primary-focus aria-invalid:border-border-destructive/75 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive:
      'bg-input dark:bg-input/35 text-destructive border border-border-destructive-accent hover:bg-input-focus dark:hover:bg-input-focus/75 focus-visible:bg-input-focus dark:focus-visible:bg-input-focus/25 focus-visible:border-border-destructive-accent-focus',
  },
  ghost: {
    default:
      'bg-transparent text-accent-foreground hover:bg-accent/80 active:bg-accent border-transparent focus-visible:border focus-visible:border-border-primary-focus aria-invalid:border-border-destructive/75 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive:
      'bg-transparent text-destructive hover:bg-destructive-accent-hover active:bg-destructive-accent-focus border-transparent focus-visible:border focus-visible:border-border-destructive-accent',
  },
  linkPrimary: {
    default:
      'bg-transparent text-foreground border-transparent focus-visible:border focus-visible:bg-input-focus focus-visible:border-border-primary-focus aria-invalid:border-border-destructive/75 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive: 'bg-transparent text-destructive border-transparent focus-visible:border focus-visible:bg-input-focus focus-visible:border-border-primary-focus',
  },
  linkSecondary: {
    default:
      'bg-transparent text-brand border-transparent focus-visible:border focus-visible:bg-input-focus focus-visible:border-border-primary-focus aria-invalid:border-border-destructive/75 aria-invalid:outline-3 aria-invalid:outline-focus-ring-destructive',
    destructive: 'bg-transparent text-destructive border-transparent focus-visible:border focus-visible:bg-input-focus focus-visible:border-border-primary-focus',
  },
};

export const buttonVariants = cva(
  'group/button text-md transition-all outline-focus-ring aria-invalid:outline-ring-destructive dark:aria-invalid:outline-focus-ring-destructive/50 relative rounded-md inline-flex cursor-pointer items-center justify-center overflow-hidden rounded-md font-medium whitespace-nowrap opacity-100 outline-0 transition-colors duration-150 focus-visible:z-10 focus-visible:outline-3 aria-invalid:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none',
  {
    variants: {
      size: {
        sm: 'h-9 min-h-9 min-w-9 gap-1.5 px-2 text-sm',
        md: 'h-10 min-h-10 min-w-10 gap-2 px-3 text-base',
      },
      variant: {
        primary: '',
        secondary: '',
        tertiary: '',
        outline: '',
        ghost: '',
        linkPrimary: '',
        linkSecondary: '',
      },
      variantColor: {
        default: '',
        destructive: '',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        variantColor: 'default',
        className: colorVariants.primary.default,
      },
      {
        variant: 'secondary',
        variantColor: 'default',
        className: colorVariants.secondary.default,
      },
      {
        variant: 'tertiary',
        variantColor: 'default',
        className: colorVariants.tertiary.default,
      },
      {
        variant: 'outline',
        variantColor: 'default',
        className: colorVariants.outline.default,
      },
      {
        variant: 'ghost',
        variantColor: 'default',
        className: colorVariants.ghost.default,
      },
      {
        variant: 'linkPrimary',
        variantColor: 'default',
        className: colorVariants.linkPrimary.default,
      },
      {
        variant: 'linkSecondary',
        variantColor: 'default',
        className: colorVariants.linkSecondary.default,
      },
      {
        variant: 'primary',
        variantColor: 'destructive',
        className: colorVariants.primary.destructive,
      },
      {
        variant: 'secondary',
        variantColor: 'destructive',
        className: colorVariants.secondary.destructive,
      },
      {
        variant: 'tertiary',
        variantColor: 'destructive',
        className: colorVariants.tertiary.destructive,
      },
      {
        variant: 'outline',
        variantColor: 'destructive',
        className: colorVariants.outline.destructive,
      },
      {
        variant: 'ghost',
        variantColor: 'destructive',
        className: colorVariants.ghost.destructive,
      },
      {
        variant: 'linkPrimary',
        variantColor: 'destructive',
        className: colorVariants.linkPrimary.destructive,
      },
      {
        variant: 'linkSecondary',
        variantColor: 'destructive',
        className: colorVariants.linkSecondary.destructive,
      },
      {
        variant: 'linkPrimary',
        className: 'px-0 hover:underline focus-visible:underline',
      },
      {
        variant: 'linkSecondary',
        className: 'px-0 hover:underline focus-visible:underline',
      },
      {
        variant: 'linkPrimary',
        size: 'md',
        className: 'h-8 min-h-8',
      },
      {
        variant: 'linkSecondary',
        size: 'md',
        className: 'h-8 min-h-8',
      },
      {
        variant: 'linkPrimary',
        size: 'sm',
        className: 'h-7 min-h-7',
      },
      {
        variant: 'linkSecondary',
        size: 'sm',
        className: 'h-7 min-h-7',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      variantColor: 'default',
      size: 'md',
    },
  }
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button(buttonProps: ButtonProps) {
  const { className, variant, variantColor, size, asChild = false, ...props } = buttonProps;
  const Comp = asChild ? Slot : 'button';

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, variantColor, size, className }))} {...props} />;
}
